import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import type { DealsGroupFindInterface } from '@zaoblako/shared/modules/business/dto/deals-group/deals-group-find.dto';
import type { ApprovalSchemeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';
import { ApprovalSchemeQueryInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-query.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { BudgetRequestCreateInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/budget-request.create.dto';
import type { BudgetRequestFindInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/budget-request-find.dto';
import { BudgetRequestStatusTypeEnum } from '@zaoblako/shared/modules/task/enum/budget.request.status.type.enum';
import { ApprovalSchemeService } from '@zaoblako/web/core/services/budget/approval-scheme.service';
import { BudgetRequestService } from '@zaoblako/web/core/services/budget/budget-request.service';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { DealGroupsService } from '@zaoblako/web/core/services/deal/deal-groups.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { ProjectService } from '@zaoblako/web/core/services/project/project.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { BudgetTaskFormComponent } from '@zaoblako/web/modules/budget/dumbs/budget-task-form/budget-task-form.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { forkJoin, Observable } from 'rxjs';

@Component({
  selector: 'crm-budget-task-form-sidebar',
  standalone: true,
  imports: [CommonModule, BudgetTaskFormComponent, SpinnerComponent],
  templateUrl: './budget-task-form-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BudgetTaskFormSidebarComponent implements OnInit {
  @Input() public readonly index: number;
  public task = <BudgetRequestCreateInterface>{};
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public schemes: ApprovalSchemeFindInterface[] = [];
  public companies: CompanyFindInterface[] = [];
  public directions: DirectionFindInterface[] = [];
  public dealGroups: DealsGroupFindInterface[] = [];
  public userGroups: UserGroupFindInterface[] = [];
  public projects: ProjectFindInterface[] = [];

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly budgetRequestService: BudgetRequestService,
    private readonly directionService: DirectionService,
    private readonly userGroupService: UserGroupService,
    private readonly companyService: CompanyService,
    private readonly dealGroupsService: DealGroupsService,
    private readonly approvalSchemeService: ApprovalSchemeService,
    private readonly projectService: ProjectService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly globalService: GlobalService,
    private readonly router: Router
  ) {}

  public ngOnInit(): void {
    const taskId = this.formStoreService.getState.forms[this.index]?.data?.taskId;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([
      this.approvalSchemeService.find<ApprovalSchemeFindInterface>(<ApprovalSchemeQueryInterface>{
        onlyWithExplicitAccess: true
      }),
      this.userGroupService.find<UserGroupFindInterface>(),
      this.directionService.find<DirectionFindInterface>({
        onlyOwn: !this.globalService.isAccessRule('BASIS:CATALOG:ACCESS_TO_ALL_DIRECTIONS')
      })
    ]).subscribe(([schemes, userGroups, directions]) => {
      this.schemes = schemes;
      this.userGroups = userGroups;
      this.directions = directions;

      if (!this.formStoreService.getState.forms[this.index]?.isEdit) {
        this.task.scheme = this.schemes?.find((scheme) => scheme.isDefault);
        this.setDefaultUsersAndAuditors();
      }
      this.cdRef.markForCheck();
    });

    forkJoin([
      this.companyService.find<CompanyFindInterface>(),
      this.dealGroupsService.flat(),
      this.projectService.search()
    ]).subscribe(([companies, dealGroups, projects]) => {
      this.companies = companies;
      this.dealGroups = dealGroups;
      this.projects = projects;
      this.cdRef.markForCheck();
    });

    if (!isNaN(taskId)) {
      this.formStoreService.setLoading(true, this.index);

      forkJoin([
        this.budgetRequestService.findById<BudgetRequestFindInterface>(taskId),
        this.budgetRequestService.getProjects(taskId)
      ]).subscribe({
        next: ([task, projects]) => {
          this.task = task;
          this.task.scheme = this.schemes.find((scheme) => scheme.id === this.task.scheme?.id);
          this.setDefaultUsersAndAuditors();
          this.task.projects = projects;
          this.task.status = BudgetRequestStatusTypeEnum.DRAFT;
          this.formStoreService.setLoading(false, this.index);
        }
      });
    }

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    forkJoin([
      this.budgetRequestService.findById<BudgetRequestFindInterface>(
        this.formStoreService.getState.forms[this.index]?.entityId
      ),
      this.budgetRequestService.getProjects(this.formStoreService.getState.forms[this.index]?.entityId)
    ]).subscribe({
      next: ([task, projects]) => {
        this.task = task;
        this.task.scheme = this.schemes.find((scheme) => scheme.id === this.task.scheme?.id);
        this.task.projects = projects;
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
      }
    });
  }

  public saveTask(task: BudgetRequestCreateInterface) {
    if (this.formStoreService.getState.forms[this.index]?.entityId) {
      this.formStoreService.setLoading(true, this.index);

      this.budgetRequestService.update<BudgetRequestCreateInterface, BudgetRequestFindInterface>(task).subscribe({
        next: (newTask) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage(
            `Заявка №${this.formStoreService.getState.forms[this.index]?.entityId} обновлена`
          );
          this.formStoreService.saveNext(newTask, this.index);
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    } else {
      this.formStoreService.setLoading(true, this.index);

      this.budgetRequestService.create<BudgetRequestFindInterface>(task).subscribe({
        next: (newTask) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage('Заявка создана');
          if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
            this.formStoreService.closeSidebar(this.index);
            this.router.navigate(['/budget/task/card/' + newTask.id]).catch(console.error);
          } else {
            this.formStoreService.saveNext(newTask, this.index);
          }
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    }
  }

  public deleteTask() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить заявку №${this.formStoreService.getState.forms[this.index]?.entityId}?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.budgetRequestService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(
              `Заявка №${this.formStoreService.getState.forms[this.index]?.entityId} удалена`
            );
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }

  private setDefaultUsersAndAuditors() {
    if (this.task.scheme) {
      if (this.task.scheme.userAuditors?.length) {
        this.task.userAuditors = this.task.scheme.userAuditors;
      }
      if (this.task.scheme?.userGroupAuditors?.length) {
        this.task.userGroupAuditors = this.task.scheme.userGroupAuditors;
      }
    }
  }
}
